import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { ICountry, IUserState } from "src/types/user";

// Async Thunk to delete an address
export const deleteAddressThunk = createAsyncThunk(
  "user/deleteAddress",
  async (
    { customerId, addressId }: { customerId: any; addressId: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await fetch(`/user-address/${customerId}/${addressId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to delete address");
      }
      return addressId; // Return the ID of the deleted address
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message); // Ensure the error is an instance of Error
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

const initialState: IUserState = {
  token: null,
  userData: {},
  addressData: null,
  error: null,
  loading: false,
  countryCode: { code: "USA", name: "USA", flag: "🇬🇧" },
  otpLoading: false,
  creditPoints: null,
  creditHistory: [],
  creditHistoryPagination: null,
  creditHistoryLoading: false,
  creditHistoryError: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    // Login Actions
    getUserAction: (state, _action: PayloadAction<any>) => {
      state.loading = true;
      state.error = null;
    },
    getUserSuccess: (
      state,
      action: PayloadAction<{ token: string; user: Record<string, any> }>
    ) => {
      state.token = action.payload.token;
      state.userData = action.payload.user;
      state.loading = false;
      state.error = null;
      if (action.payload.user.country === "Canada") {
        state.countryCode = { code: "CA", name: "Canada", flag: "🇨🇦" };
      }
    },
    getUserFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Registration Actions
    getRegisterAction: (state, _action: PayloadAction<any>) => {
      state.loading = true;
      state.error = null;
    },
    getRegisterSuccess: (state, _action: PayloadAction<any>) => {
      state.loading = false;
      state.error = null;
    },
    getRegisterFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Address Actions
    getAddressAction: (state, _action: PayloadAction<any>) => {
      state.loading = true;
      state.error = null;
    },
    getAddressSuccess: (
      state,
      action: PayloadAction<{ data: { addresses: any[] } }>
    ) => {
      state.addressData = action.payload.data.addresses;
      state.loading = false;
      state.error = null;
    },
    getAddressFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Post Address Actions
    postAddressAction: (state, action: PayloadAction<any>) => {
      state.loading = true;
      state.error = null;
    },
    postAddressSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = null;
    },
    postAddressFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Delete Address Actions
    deleteAddressAction: (state, _action: PayloadAction<any>) => {
      state.loading = true;
      state.error = null;
    },
    deleteAddressSuccess: (state, action: PayloadAction<number>) => {
      state.loading = false;
      state.addressData = state.addressData.filter(
        (address: any) => address.id !== action.payload
      );
      state.error = null;
    },
    deleteAddressFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Logout Action
    logout: (state) => {
      state.token = null;
      state.userData = {};
      state.addressData = null;
      state.error = null;
      state.loading = false;
      state.creditPoints = null;
      state.creditHistory = [];
      state.creditHistoryPagination = null;
      state.creditHistoryLoading = false;
      state.creditHistoryError = null;
    },
    setCountryCode: (state, action: PayloadAction<ICountry>) => {
      state.countryCode = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUserProfile: (state, action: PayloadAction<any>) => {
      state.loading = true;
      state.error = null;
    },
    updateUserProfileSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.userData = { ...state.userData, ...action.payload };
      state.error = null;
    },
    updateUserProfileFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    sendOtp: (state, _action: PayloadAction<any>) => {
      state.otpLoading = true;
      state.error = null;
    },
    sendOtpSuccess: (state, action: PayloadAction<any>) => {
      state.otpLoading = false;
      state.error = null;
    },
    sendOtpFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.otpLoading = false;
    },
    resetPassword: (state, _action: PayloadAction<any>) => {
      state.error = null;
    },
    resetPasswordSuccess: (state, action: PayloadAction<any>) => {
      state.error = null;
      state.userData = { ...state.userData, ...action.payload };
    },
    resetPasswordFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.otpLoading = false;
    },
    getCreditPointsAction: (state) => {},
    getCreditPointsSuccess: (state, action: PayloadAction<any>) => {
      state.creditPoints = action?.payload?.creditPoints ?? 0;
    },
    getCreditPointsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    getCreditHistoryAction: (state, _action: PayloadAction<any>) => {
      state.creditHistoryLoading = true;
      state.creditHistoryError = null;
    },
    getCreditHistorySuccess: (state, action: PayloadAction<any>) => {
      state.creditHistory = action?.payload?.data ?? [];
      state.creditHistoryPagination = action?.payload?.pagination ?? null;
      state.creditHistoryLoading = false;
    },
    getCreditHistoryFailure: (state, action: PayloadAction<string>) => {
      state.creditHistoryError = action.payload;
      state.creditHistoryLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteAddressThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteAddressThunk.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.loading = false;
          state.addressData = state.addressData.filter(
            (address: any) => address.id !== action.payload
          );
          state.error = null;
        }
      )
      .addCase(deleteAddressThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  getUserAction,
  getUserSuccess,
  getUserFailure,
  logout,
  getRegisterAction,
  getRegisterSuccess,
  getRegisterFailure,
  getAddressAction,
  getAddressSuccess,
  getAddressFailure,
  postAddressAction,
  postAddressSuccess,
  postAddressFailure,
  deleteAddressAction,
  deleteAddressSuccess,
  deleteAddressFailure,
  setCountryCode,
  clearError,
  updateUserProfile,
  updateUserProfileSuccess,
  updateUserProfileFailure,
  sendOtp,
  sendOtpSuccess,
  sendOtpFailure,
  resetPassword,
  resetPasswordSuccess,
  resetPasswordFailure,
  getCreditPointsAction,
  getCreditPointsSuccess,
  getCreditPointsFailure,
  getCreditHistoryAction,
  getCreditHistorySuccess,
  getCreditHistoryFailure,
} = userSlice.actions;

export default userSlice.reducer;
